/* The Hooper Buddy icon set. One inline SVG per name, drawn by hand on a
 * 24 by 24 grid so navigation and transport controls look the same on every
 * machine instead of inheriting whatever emoji the operating system ships.
 *
 * The navigation glyphs are small colour illustrations, not line glyphs. The
 * emoji they replaced were warm because they were coloured, and a rail of
 * single hue outlines read flat next to them. Each one now has a body in the
 * section hue plus an accent or two of its own, so the five coloured groups
 * still read as groups and every icon still has some life in it.
 *
 * House rules for every glyph in here:
 *  - The dominant colour is always currentColor, so the button decides it.
 *    The rail sets that to the section hue, and the hover and active states in
 *    app.css keep working in both themes for free.
 *  - No colour literal ever appears in this file. Accents are class names, and
 *    icons.css maps each class to a custom property with a value per theme.
 *  - a 2px stroke, round cap, round join, on a filled body. A few accents
 *    carry their own stroke-width attribute where 2px closed up at 20px.
 *  - soft, generous geometry. This is an app a twelve year old and their
 *    parent share, so the shapes are plump and friendly, not hairline.
 *  - readable at 20px, which is what the rail actually renders. Two colours
 *    beat four, and being different from your neighbour beats being detailed.
 *  - an accent is an area, not a line. On a plain screen a 2px stroke in this
 *    grid lands on 1.7 device pixels and antialiasing eats half of it, so a
 *    detail that only exists as a thin line is a detail that is not there.
 *
 * The classes, all defined in icons.css:
 *   i-w      body, currentColor at the wash opacity, keeps its stroke
 *   i-f      body, solid currentColor
 *   i-n      no stroke, for a shape that is pure fill
 *   i-sun i-berry i-leaf i-sky i-bark i-eye     an accent fill
 *   i-s-sun i-s-berry i-s-leaf i-s-bark         an accent stroke
 *
 * Transport controls (play, pause, mic, send and the rest) stay pure outline.
 * They are controls, not pictures, and their buttons already tint them.
 *
 * Use it two ways. In markup, put an empty holder where the glyph was and let
 * paint() fill it once at boot:
 *     <button data-view="train"><i data-icon="train" aria-hidden="true"></i></button>
 * In a template string, call icon() directly:
 *     `<button>${icon('play', 14)} ${title}</button>`
 */

/* Drawn once, reused wherever a heart belongs. */
const HEART = 'M12 20.3 4.9 13.4a5 5 0 1 1 7.1-5.8 5 5 0 1 1 7.1 5.8z';

const PATHS = {
  /* home: plump walls in the row hue, a red roof over them and a blue arched
     door. The roof is drawn after the walls so it covers the wall top line. */
  home: '<path class="i-w" d="M5.6 11.4h12.8v7.1a1.6 1.6 0 0 1-1.6 1.6H7.2a1.6 1.6 0 0 1-1.6-1.6z"/><path class="i-berry" d="M12 4.3 21.3 11.6H2.7z"/><path class="i-sky" d="M9.9 20.1v-4.1a2.1 2.1 0 0 1 4.2 0v4.1z"/>',

  /* train: the board, on its own. It used to have an arrow in it and the arrow
     is what broke it. A shaft leaving the ring at an angle is the silhouette
     of a handle, so at 20px the whole thing read as a frying pan, and making
     the head a filled triangle did not help: the head and the shaft are one
     gold diagonal once antialiasing has had them. The dart is gone and what is
     left is what a target actually is, a ring around a ring around the middle.
     Two of the three are red, which is the second colour and also the part you
     aim at. The rings are not all the same hue on purpose: three concentric
     strokes in one colour do turn to mush at this size. */
  train: '<circle class="i-w" cx="12" cy="12" r="8.6"/><circle class="i-s-berry" stroke-width="2.4" cx="12" cy="12" r="5.4"/><circle class="i-berry i-n" cx="12" cy="12" r="2.2"/>',

  /* sessions: the page of finished work. The tick is green because that is
     what a finished thing looks like, and it is the fastest read on the page. */
  sessions: '<rect class="i-w" x="4.4" y="3.6" width="15.2" height="16.8" rx="3.4"/><path class="i-s-leaf" d="M7.9 9.4 9.6 11.1 12.9 7.7"/><path d="M8.2 14.6h7.6"/><path d="M8.2 17.6h4.8"/>',

  /* vault: a clapperboard with a blue slate and a gold play mark in the body,
     for the clips that live in there. The body fill and its outline are two
     elements so the slate can sit on top without eating the outline. */
  vault: '<rect class="i-w i-n" x="3.4" y="9.8" width="17.2" height="10.6" rx="2.8"/><path class="i-sky" d="M3.4 9.8V6.2a2.4 2.4 0 0 1 2.4-2.4h12.4a2.4 2.4 0 0 1 2.4 2.4v3.6z"/><rect x="3.4" y="9.8" width="17.2" height="10.6" rx="2.8"/><path d="M9.6 3.9 7.5 9.7"/><path d="M15 3.9 12.9 9.7"/><path class="i-sun i-n" d="M9.9 12.4 16.1 15.5 9.9 18.6z"/>',

  /* companion: the ball, with the face it actually has in the app. This is the
     one icon that is a character, so it gets the most of itself: bright eyes
     that read against either rail, two warm cheeks and a proper smile. */
  companion: '<circle class="i-w" cx="12" cy="12" r="8.4"/><path d="M5.3 6.9a8 8 0 0 1 0 10.2"/><path d="M18.7 6.9a8 8 0 0 0 0 10.2"/><circle class="i-berry i-n" cx="8.3" cy="13.5" r="1.2"/><circle class="i-berry i-n" cx="15.7" cy="13.5" r="1.2"/><circle class="i-eye i-n" cx="9.6" cy="10.3" r="1.35"/><circle class="i-eye i-n" cx="14.4" cy="10.3" r="1.35"/><path d="M9.7 14.9a3 3 0 0 0 4.6 0"/>',

  /* brain: the memory map, which is what the view actually draws. The two
     lobes it started as closed into a round blob at 20px, right next to the
     round companion ball in the same section, so it became a hub with
     memories hung off it. Those memories were scattered off centre with a
     stray extra line, and at 20px that was a handful of dots nobody could
     name. One hub and three spokes at even angles is the same idea with a
     silhouette you can recognise from across the rail. */
  brain: '<path d="M12 11.6 11.6 5.4"/><path d="M12 11.6 5.9 16.1"/><path d="M12 11.6 18.3 15.5"/><circle class="i-f i-n" cx="12" cy="11.6" r="3.3"/><circle class="i-sun i-n" cx="11.5" cy="5" r="2.5"/><circle class="i-leaf i-n" cx="5.4" cy="16.4" r="2.5"/><circle class="i-sky i-n" cx="18.7" cy="15.8" r="2.5"/>',

  /* journal: the book open on the desk, not shut on the shelf. Closed, it was
     one more rounded rectangle next to Sessions and the Vault. The bookmark
     is the bit of colour, and it says which page you were on. */
  journal: '<path class="i-w" d="M12 7.4C10.4 6 8.3 5.3 5.6 5.3a1.4 1.4 0 0 0-1.4 1.4v10.1a1.4 1.4 0 0 0 1.4 1.4c2.7 0 4.8.7 6.4 2.1 1.6-1.4 3.7-2.1 6.4-2.1a1.4 1.4 0 0 0 1.4-1.4V6.7a1.4 1.4 0 0 0-1.4-1.4c-2.7 0-4.8.7-6.4 2.1z"/><path class="i-sky i-n" d="M14.4 7.6h3.4v6.6l-1.7-1.5-1.7 1.5z"/><path d="M12 7.4v12.9"/>',

  /* nutrition: an apple, the friendliest thing on a plate. Brown stem, green
     leaf, body in the section hue. The stem and the leaf used to touch and
     came out as one brown and green smudge on the shoulder of the fruit, so
     the stem is thicker and taller and the leaf is bigger and set clear of it.
     The leaf is close to the green of its own section on purpose: the stem is
     what carries the second colour here. */
  nutrition: '<path class="i-w" d="M12 7.9a4.1 4.1 0 0 0-3.4-.7C6.4 7.7 5.1 9.9 5.5 12.6c.5 3.2 2.5 6.4 4.4 6.9.9.2 1.4-.3 2.1-.3s1.2.5 2.1.3c1.9-.5 3.9-3.7 4.4-6.9.4-2.7-.9-4.9-3.1-5.4a4.1 4.1 0 0 0-3.4.7z"/><path class="i-s-bark" stroke-width="2.4" d="M11.9 8.1V4.5"/><path class="i-leaf i-n" d="M13 6.9c.2-2.7 2.4-4.4 5.1-4.2.4 2.7-1.8 4.7-4.4 4.8z"/>',

  /* health carries the pulse across it, in red, so it is not the same glyph as
     the like button on the radio. The pulse is two beats tall and drawn a
     little heavier than the outline: the old one turned every vertex over in
     four units of height, and at 20px that closed up into a red smudge in the
     middle of the heart instead of reading as a trace. */
  health: `<path class="i-w" d="${HEART}"/><path class="i-s-berry" stroke-width="2.3" d="M6.6 12.6h2.9l1.9-3.9 2.3 6.4 1.6-2.5h2.3"/>`,
  like: `<path d="${HEART}"/>`,

  /* calendar: the pad with two little ears, a red header and today in gold.
     The fill and the outline are separate so the header can cover the fill
     without covering the frame. */
  calendar: '<rect class="i-w i-n" x="3.6" y="5.4" width="16.8" height="15" rx="3.2"/><path class="i-berry i-n" d="M6.8 5.4h10.4a3.2 3.2 0 0 1 3.2 3.2v1.5H3.6V8.6a3.2 3.2 0 0 1 3.2-3.2z"/><rect x="3.6" y="5.4" width="16.8" height="15" rx="3.2"/><path d="M3.6 10.1h16.8"/><path d="M8.4 3.2v3.6"/><path d="M15.6 3.2v3.6"/><circle class="i-sun i-n" cx="8.4" cy="14" r="1.3"/><circle class="i-f i-n" cx="12" cy="14" r="1.15"/><circle class="i-f i-n" cx="15.6" cy="14" r="1.15"/><circle class="i-f i-n" cx="8.4" cy="17.5" r="1.15"/>',

  /* record: the player's card. A portrait panel with a face bar and two lines
     of stats beside it, so it reads as a profile rather than a document. It is
     deliberately nothing like `compete`: those two sit four rows apart and were
     the same trophy until now, told apart only by their section hue. */
  record: '<rect class="i-w i-n" x="3.2" y="4.6" width="17.6" height="14.8" rx="3"/><rect x="3.2" y="4.6" width="17.6" height="14.8" rx="3"/><circle class="i-berry i-n" cx="8.6" cy="10.4" r="2.5"/><path class="i-berry i-n" d="M4.9 17.2a3.9 3.9 0 0 1 7.4 0z"/><path class="i-sun" d="M14.6 9.4h4.1"/><path d="M14.6 13h4.1"/><path d="M14.6 16.2h2.6"/>',

  /* skills: three rising steps with the top one lit, for working a skill up
     rather than knowing a thing. `brain` was doing double duty here and the
     two sat in different sections looking identical. */
  skills: '<rect class="i-f i-n" x="3.4" y="14.4" width="5" height="6.2" rx="1.6"/><rect x="3.4" y="14.4" width="5" height="6.2" rx="1.6"/><rect class="i-sky i-n" x="9.5" y="10.2" width="5" height="10.4" rx="1.6"/><rect x="9.5" y="10.2" width="5" height="10.4" rx="1.6"/><rect class="i-sun i-n" x="15.6" y="5.2" width="5" height="15.4" rx="1.6"/><rect x="15.6" y="5.2" width="5" height="15.4" rx="1.6"/>',
  /* body: the Body Map (Analytics). A draft glyph in the house style. */
  body: '<circle class="i-f i-n" cx="12" cy="5.2" r="2.6"/><path class="i-w" d="M8.4 9.6h7.2l-.9 6.2H9.3z"/><path d="M8.4 9.6 5.2 6.4"/><path d="M15.6 9.6l3.2-3.2"/><path d="M10 15.8l-1 5"/><path d="M14 15.8l1 5"/><circle class="i-sky i-n" cx="18.8" cy="6.4" r="1.9"/>',

  /* compete: a gold cup with both handles, on a solid base in the section hue
     so the row colour still has a body to sit in. */
  compete: '<path class="i-sun" d="M7.8 4.2h8.4v5.4a4.2 4.2 0 0 1-8.4 0z"/><path d="M7.8 5.8H5.7a2.3 2.3 0 0 0 2.4 3.3"/><path d="M16.2 5.8h2.1a2.3 2.3 0 0 1-2.4 3.3"/><path d="M12 13.8v2.5"/><path class="i-f" d="M8.4 20.2h7.2a3.6 3.6 0 0 0-3.6-3.9 3.6 3.6 0 0 0-3.6 3.9z"/>',

  /* music: two notes, beamed, with round heads in two colours. The heads are
     solid so they stay two clear dots at 20px instead of two rings. */
  music: '<path d="M9.6 17.6V6.4l9-1.9v11.2"/><path d="M9.6 9.1 18.6 7.2"/><circle class="i-sky i-n" cx="7.2" cy="17.6" r="2.7"/><circle class="i-sun i-n" cx="16.2" cy="15.7" r="2.7"/>',

  /* settings: a six tooth gear, generated on the grid so the teeth are even,
     with a gold hub in the middle. */
  settings: '<path class="i-w" d="M10.1 3A9.2 9.2 0 0 1 13.9 3L14.2 6A6.4 6.4 0 0 1 16.1 7.1L18.8 5.8A9.2 9.2 0 0 1 20.7 9.2L18.3 10.9A6.4 6.4 0 0 1 18.3 13.1L20.7 14.8A9.2 9.2 0 0 1 18.8 18.2L16.1 16.9A6.4 6.4 0 0 1 14.2 18L13.9 21A9.2 9.2 0 0 1 10.1 21L9.8 18A6.4 6.4 0 0 1 7.9 16.9L5.2 18.2A9.2 9.2 0 0 1 3.3 14.8L5.7 13.1A6.4 6.4 0 0 1 5.7 10.9L3.3 9.2A9.2 9.2 0 0 1 5.2 5.8L7.9 7.1A6.4 6.4 0 0 1 9.8 6L10.1 3Z"/><circle class="i-sun i-n" cx="12" cy="12" r="2.9"/>',

  /* signout: the door with a little window, and the gold way out of it. The
     fill and the outline are separate because the outline is deliberately
     open on the right, which is where the arrow leaves. */
  signout: '<path class="i-w i-n" d="M13.4 4.2H7.3a2.2 2.2 0 0 0-2.2 2.2v11.2a2.2 2.2 0 0 0 2.2 2.2h6.1z"/><rect class="i-sky i-n" x="7.3" y="7.3" width="3" height="3" rx=".9"/><path d="M13.4 4.2H7.3a2.2 2.2 0 0 0-2.2 2.2v11.2a2.2 2.2 0 0 0 2.2 2.2h6.1"/><path class="i-s-sun" d="M17.2 15.4 20.6 12l-3.4-3.4"/><path class="i-s-sun" d="M20.2 12h-9.8"/>',

  /* mic: capsule, cradle, stand */
  mic: '<rect x="9.2" y="3.2" width="5.6" height="10.4" rx="2.8"/><path d="M6.4 11.3a5.6 5.6 0 0 0 11.2 0"/><path d="M12 16.9v3.4"/><path d="M9.2 20.3h5.6"/>',

  /* expand: four corners pulling out, and the same mark reads as collapse
     when the button is flipped, which is why there is only one of them */
  expand: '<path d="M3.6 9.1V3.6h5.5"/><path d="M20.4 9.1V3.6h-5.5"/><path d="M3.6 14.9v5.5h5.5"/><path d="M20.4 14.9v5.5h-5.5"/>',

  /* panel: a window with a side panel, for bringing the room's panel back */
  panel: '<rect x="3.4" y="4.6" width="17.2" height="14.8" rx="2.6"/><path d="M14.6 4.6v14.8"/>',

  /* collapse: the same four corners pointing back in */
  collapse: '<path d="M9.1 3.6v5.5H3.6"/><path d="M14.9 3.6v5.5h5.5"/><path d="M9.1 20.4v-5.5H3.6"/><path d="M14.9 20.4v-5.5h5.5"/>',

  /* send: a paper plane with one fold */
  send: '<path d="M20.6 3.8 14.5 20.6 11 13.4 3.8 9.9z"/><path d="M20.6 3.8 11 13.4"/>',

  close: '<path d="M6.6 6.6 17.4 17.4"/><path d="M17.4 6.6 6.6 17.4"/>',
  play: '<path d="M8.6 5.4 19 12 8.6 18.6z"/>',
  pause: '<path d="M9.4 5.6v12.8"/><path d="M14.6 5.6v12.8"/>',
  stop: '<rect x="6.4" y="6.4" width="11.2" height="11.2" rx="2.6"/>',
  next: '<path d="M6.6 5.8 15 12 6.6 18.2z"/><path d="M18 5.8v12.4"/>',

  /* rewind: a full turn backwards, which is what the back button really does */
  rewind: '<path d="M3.4 5.6V11h5.4"/><path d="M5.1 15.1a8 8 0 1 0 1.9-8.3L3.4 11"/>',

  shuffle: '<path d="M15.6 4h4.4v4.4"/><path d="M3.6 20.4 20 4"/><path d="M20 15.6V20h-4.4"/><path d="M14.6 14.6 20 20"/><path d="M3.6 3.6 9 9"/>',
  repeat: '<path d="M16.6 3 20.4 6.8 16.6 10.6"/><path d="M3.6 11.2V9.4a3.6 3.6 0 0 1 3.6-3.6h13.2"/><path d="M7.4 21 3.6 17.2 7.4 13.4"/><path d="M20.4 12.8v1.8a3.6 3.6 0 0 1-3.6 3.6H3.6"/>',
  'repeat-one': '<path d="M16.6 3 20.4 6.8 16.6 10.6"/><path d="M3.6 11.2V9.4a3.6 3.6 0 0 1 3.6-3.6h13.2"/><path d="M7.4 21 3.6 17.2 7.4 13.4"/><path d="M20.4 12.8v1.8a3.6 3.6 0 0 1-3.6 3.6H3.6"/><path d="M11 10.7 12.4 9.8v4.5"/>',

  plus: '<path d="M12 5.4v13.2"/><path d="M5.4 12h13.2"/>',
  chevron: '<path d="M9.4 5.6 15.8 12 9.4 18.4"/>',
};

/** Every name this set draws, in the order they are declared. */
export const NAMES = Object.keys(PATHS);

/**
 * One icon as an SVG string. Returns '' for a name that does not exist, so a
 * typo leaves a gap instead of throwing halfway through a render.
 */
export function icon(name, size = 20) {
  // Own keys only. A plain lookup answers "toString" and "constructor" with
  // something from Object.prototype, and that function source would end up
  // inside the svg instead of the empty string this promises.
  const body = Object.hasOwn(PATHS, name) ? PATHS[name] : null;
  if (!body) return '';
  const s = Number(size) || 20;
  return `<svg class="hb-i" viewBox="0 0 24 24" width="${s}" height="${s}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${body}</svg>`;
}

/**
 * Fill every <i data-icon="name"> holder under root. Optional data-size sets
 * the pixel size for that one holder. Safe to call again after new markup.
 */
export function paint(root = document) {
  root.querySelectorAll('[data-icon]').forEach((el) => {
    const svg = icon(el.dataset.icon, el.dataset.size);
    if (svg) el.innerHTML = svg;
  });
}
